/**
 * GitHubIssueService — Creates GitHub issues from voice feedback sessions.
 *
 * Issues are created from IssueProposals (which may aggregate multiple voice
 * comments). Issue creation and comment posting are proxied through Firebase
 * Cloud Functions to keep the GitHub PAT server-side.
 */

import { getCallable } from "../lib/callFunction";
import type {
  EnvironmentInfo,
  FeedbackSession,
  IssueProposal,
  IssueResult,
  SaveResult,
  VoiceComment,
} from "../types/feedback";
import { FeedbackRateLimiter } from "./FeedbackRateLimiter";
import { GeminiFeedbackSummaryService } from "./GeminiFeedbackSummaryService";
import { captureEnvironmentInfo } from "../utils/environmentInfo";

const ISSUE_DELAY_MS = 500;
const LABEL_NAME = "voice-feedback";
const MAX_COMMENT_BODY_CHARS = 60000;

export class GitHubIssueService {
  private rateLimiter: FeedbackRateLimiter;
  private summaryService: GeminiFeedbackSummaryService;

  constructor() {
    this.rateLimiter = new FeedbackRateLimiter();
    this.summaryService = new GeminiFeedbackSummaryService();
  }

  /** Save an entire feedback session as GitHub issues. */
  async saveSession(
    session: FeedbackSession,
    onProgress?: (completed: number, total: number) => void
  ): Promise<SaveResult> {
    const { comments } = session;
    const total = comments.length;

    // Pre-check rate limits
    const limitCheck = this.rateLimiter.canCreateIssues(total);
    if (!limitCheck.allowed) {
      return {
        totalComments: total,
        issuesCreated: 0,
        failures: comments.map((_c) => ({
          success: false,
          error: limitCheck.reason ?? "Rate limit exceeded",
          issueNumber: undefined,
          issueUrl: undefined,
        })),
        results: comments.map(() => ({
          success: false,
          error: limitCheck.reason ?? "Rate limit exceeded",
        })),
      };
    }

    const results: IssueResult[] = [];
    let issuesCreated = 0;

    for (let i = 0; i < comments.length; i++) {
      const comment = comments[i];
      const result = await this.createIssue(comment, session, total);
      results.push(result);

      if (result.success) {
        issuesCreated++;
      }

      onProgress?.(i + 1, total);

      // Delay between issues (except after the last one)
      if (i < comments.length - 1) {
        await this.delay(ISSUE_DELAY_MS);
      }
    }

    // Record usage for rate limiting
    this.rateLimiter.recordIssuesCreated(issuesCreated);

    return {
      totalComments: total,
      issuesCreated,
      failures: results.filter((r) => !r.success),
      results,
    };
  }

  /** Save pre-aggregated issue proposals as GitHub issues. */
  async saveProposals(
    proposals: IssueProposal[],
    session: FeedbackSession,
    onProgress?: (completed: number, total: number) => void
  ): Promise<SaveResult> {
    const total = proposals.length;

    const limitCheck = this.rateLimiter.canCreateIssues(total);
    if (!limitCheck.allowed) {
      return {
        totalComments: session.comments.length,
        issuesCreated: 0,
        failures: proposals.map(() => ({
          success: false,
          error: limitCheck.reason ?? "Rate limit exceeded",
          issueNumber: undefined,
          issueUrl: undefined,
        })),
        results: proposals.map(() => ({
          success: false,
          error: limitCheck.reason ?? "Rate limit exceeded",
        })),
      };
    }

    const results: IssueResult[] = [];
    let issuesCreated = 0;

    for (let i = 0; i < proposals.length; i++) {
      const proposal = proposals[i];
      const sourceComments = session.comments.filter((c) =>
        proposal.sourceCommentIndices.includes(c.index)
      );
      const envInfo = captureEnvironmentInfo();
      const result = await this.createProposalIssue(
        proposal,
        sourceComments,
        session,
        envInfo
      );
      results.push(result);

      if (result.success) {
        issuesCreated++;
      }

      onProgress?.(i + 1, total);

      if (i < proposals.length - 1) {
        await this.delay(ISSUE_DELAY_MS);
      }
    }

    this.rateLimiter.recordIssuesCreated(issuesCreated);

    return {
      totalComments: session.comments.length,
      issuesCreated,
      failures: results.filter((r) => !r.success),
      results,
    };
  }

  /** Get current rate limit status. */
  getRateLimitStatus() {
    return this.rateLimiter.getStatus();
  }

  private async createIssue(
    comment: VoiceComment,
    session: FeedbackSession,
    totalComments: number
  ): Promise<IssueResult> {
    const draft = await this.summaryService.generateIssueDraft(
      comment,
      session,
      totalComments
    );
    const title =
      draft.title ||
      comment.title ||
      `Voice Feedback — Comment ${comment.index}`;
    const envInfo = captureEnvironmentInfo();
    const body = this.formatIssueBody(
      comment,
      session,
      totalComments,
      draft,
      envInfo
    );

    try {
      const callable = getCallable<
        { title: string; body: string; labels: string[] },
        { number: number; html_url: string }
      >("feedbackCreateIssue");

      const result = await callable({
        title,
        body,
        labels: [LABEL_NAME],
      });

      const { number: issueNumber, html_url: issueUrl } = result.data;

      await this.attachFullScreenContextComments(issueNumber, comment);

      return {
        success: true,
        issueNumber,
        issueUrl,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: classifyCallableError(error),
      };
    }
  }

  private formatIssueBody(
    comment: VoiceComment,
    session: FeedbackSession,
    totalComments: number,
    draft: {
      description: string;
      suggestions: string[];
      triageSummary: string;
    },
    envInfo: EnvironmentInfo
  ): string {
    const uniqueRoutes = Array.from(
      new Set(comment.screenCaptures.map((screen) => screen.route))
    );
    const screenRoutes = uniqueRoutes.join(", ");

    const body = `Voice Feedback

| Field | Value |
| --- | --- |
| **Session** | ${session.sessionName} |
| **Comment** | Comment ${comment.index} of ${totalComments} |
| **Recorded** | ${comment.recordedAt} |
| **Duration** | ${comment.durationSeconds}s |
| **Screen(s)** | ${screenRoutes || "N/A"} |

---

Environment

| Field | Value |
| --- | --- |
| **Device** | ${envInfo.deviceType} |
| **Platform** | ${envInfo.platform} |
| **Domain** | ${envInfo.urlDomain} |

---

AI Triage Summary

${draft.triageSummary}

---

Issue Description

${draft.description}

---

Suggested Next Steps

${
  draft.suggestions.length > 0
    ? draft.suggestions.map((item) => `- ${item}`).join("\n")
    : "- No additional suggestions generated."
}

---

Screen Context

Full screen context has been attached in issue comments as HTML snapshot blocks.

---

Metadata

- **Session started:** ${session.startedAt}
- **Total comments in session:** ${totalComments}
- **User agent:** ${navigator.userAgent}
- **Feature:** Voice Feedback Capture`;

    return body;
  }

  private async createProposalIssue(
    proposal: IssueProposal,
    sourceComments: VoiceComment[],
    session: FeedbackSession,
    envInfo: EnvironmentInfo
  ): Promise<IssueResult> {
    const title = proposal.title || "Voice Feedback";
    const body = this.formatProposalBody(
      proposal,
      sourceComments,
      session,
      envInfo
    );

    try {
      const labels =
        proposal.labels.length > 0 ? proposal.labels : [LABEL_NAME];

      const callable = getCallable<
        { title: string; body: string; labels: string[] },
        { number: number; html_url: string }
      >("feedbackCreateIssue");

      const result = await callable({ title, body, labels });
      const { number: issueNumber, html_url: issueUrl } = result.data;

      // Attach screen context from all source comments
      for (const comment of sourceComments) {
        await this.attachFullScreenContextComments(issueNumber, comment);
      }

      return {
        success: true,
        issueNumber,
        issueUrl,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: classifyCallableError(error),
      };
    }
  }

  private formatProposalBody(
    proposal: IssueProposal,
    sourceComments: VoiceComment[],
    session: FeedbackSession,
    envInfo: EnvironmentInfo
  ): string {
    const allRoutes = Array.from(
      new Set(
        sourceComments.flatMap((c) => c.screenCaptures.map((s) => s.route))
      )
    );

    return `Voice Feedback

| Field | Value |
| --- | --- |
| **Session** | ${session.sessionName} |
| **Source comments** | ${proposal.sourceCommentIndices.join(", ")} |
| **Screen(s)** | ${allRoutes.join(", ") || "N/A"} |

---

Environment

| Field | Value |
| --- | --- |
| **Device** | ${envInfo.deviceType} |
| **Platform** | ${envInfo.platform} |
| **Domain** | ${envInfo.urlDomain} |

---

AI Triage Summary

${proposal.triageSummary}

---

Issue Description

${proposal.description}

---

Suggested Next Steps

${
  proposal.suggestions.length > 0
    ? proposal.suggestions.map((item) => `- ${item}`).join("\n")
    : "- No additional suggestions generated."
}

---

Screen Context

Full screen context has been attached in issue comments as HTML snapshot blocks.

---

Metadata

- **Session started:** ${session.startedAt}
- **Total comments in session:** ${session.comments.length}
- **User agent:** ${navigator.userAgent}
- **Feature:** Voice Feedback Capture`;
  }

  private async attachFullScreenContextComments(
    issueNumber: number,
    comment: VoiceComment
  ): Promise<void> {
    for (
      let screenIndex = 0;
      screenIndex < comment.screenCaptures.length;
      screenIndex++
    ) {
      const screen = comment.screenCaptures[screenIndex];
      const screenNumber = screenIndex + 1;

      const prefix = [
        `Screen Context ${screenNumber}`,
        "",
        `Page title: ${screen.pageTitle}`,
        `Route: ${screen.route}`,
        `Viewport: ${screen.viewport.width} × ${screen.viewport.height}`,
        `Captured: ${screen.capturedAt}`,
        "",
      ].join("\n");

      const html = screen.htmlSnapshot || "";
      const chunks = splitIntoChunks(
        html,
        MAX_COMMENT_BODY_CHARS - prefix.length - 80
      );

      if (chunks.length === 0) {
        await this.postIssueComment(
          issueNumber,
          `${prefix}HTML Snapshot:\n\n\`\`\`html\n\n\`\`\``
        );
        continue;
      }

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunkNumber = chunkIndex + 1;
        const totalChunks = chunks.length;
        const chunkLabel =
          totalChunks > 1
            ? `HTML Snapshot part ${chunkNumber} of ${totalChunks}`
            : "HTML Snapshot";

        const commentBody = `${prefix}${chunkLabel}:\n\n\`\`\`html\n${chunks[chunkIndex]}\n\`\`\``;
        await this.postIssueComment(issueNumber, commentBody);
      }
    }
  }

  private async postIssueComment(
    issueNumber: number,
    body: string
  ): Promise<void> {
    const callable = getCallable<
      { issueNumber: number; body: string },
      { success: boolean }
    >("feedbackPostComment");

    await callable({ issueNumber, body });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

function splitIntoChunks(text: string, chunkSize: number): string[] {
  if (!text) {
    return [];
  }

  const chunks: string[] = [];
  let currentIndex = 0;

  while (currentIndex < text.length) {
    chunks.push(text.slice(currentIndex, currentIndex + chunkSize));
    currentIndex += chunkSize;
  }

  return chunks;
}

/**
 * Classify a callable error into an actionable user-facing message.
 *
 * Firebase Cloud Functions errors arrive with `code: "functions/<status>"`.
 * We map those to specific prefixes so the UI can distinguish validation
 * failures from server/network issues.
 */
function classifyCallableError(error: unknown): string {
  const code =
    error !== null &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "string"
      ? (error as { code: string }).code
      : undefined;

  const message = error instanceof Error ? error.message : "Unknown error";

  if (code === "functions/invalid-argument") {
    return `Validation error: ${message}`;
  }

  if (
    code === "functions/internal" ||
    code === "functions/unavailable" ||
    code === "functions/deadline-exceeded"
  ) {
    return `Server error: ${message}`;
  }

  return `Network error: ${message}`;
}
